import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Form, 
  Input, 
  InputNumber, 
  notification,
  Spin,
  Space,
  Tag,
  Popconfirm,
  Card,
  Modal,
  Typography,
  Select
} from 'antd';

import { 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  ScanOutlined,
  EditFilled,
  BarcodeOutlined
} from '@ant-design/icons';

import { database, ref, get, set, remove, onValue, update } from '../../Auth/db/firebase';
import { useParams } from 'react-router-dom';
import { getCustomCurrencies, formatPriceWithCurrency, preloadCustomCurrencies } from '../../../utils/currency';

// Import reusable components
import DataTable from '../../../components/shared/DataTable';
import FormModal from '../../../components/shared/FormModal';
import QuantityControl from '../../../components/shared/QuantityControl';
import IndividualItemsDropdown from '../../../components/shared/IndividualItemsDropdown';
import QRCodeModal from '../../../components/shared/QRCodeModal';
import BarcodeModal from '../../../components/shared/BarcodeModal';
import SimpleBarcodeScanner from '../../../components/shared/SimpleBarcodeScanner';

const { Text } = Typography;

const Items = ({ userData }) => {
  const { facilityId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedItemForQR, setSelectedItemForQR] = useState(null);
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [selectedItemForBarcode, setSelectedItemForBarcode] = useState(null);
  const [itemDetailModalVisible, setItemDetailModalVisible] = useState(false);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();
  
  // Barcode scanning states
  const [inputMode, setInputMode] = useState('manual'); // 'manual' or 'scan'
  const [barcodeScannerVisible, setBarcodeScannerVisible] = useState(false);
  const [, setScannedBarcode] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(userData?.defaultCurrency || 'USD');
  const [customCurrencies, setCustomCurrencies] = useState([]);

  const [api, contextHolder] = notification.useNotification();

  const openNotification = (type, message) => {
    api[type]({
      message,
      placement: 'bottomRight',
    });
  };

    // Generate base ID for item type (without individual item numbers)
  const generateBaseItemId = (category, name, existingItems) => {
    const today = new Date();
    const year = String(today.getFullYear()).slice(-2);
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateStr = year + month + day; // YYMMDD format
    
    // Smart category abbreviation to handle conflicts
    const generateCategoryAbb = (cat) => {
      const cleanCat = cat.replace(/[^a-zA-Z]/g, '').toUpperCase();
      let abb = cleanCat.slice(0, 2);
      
      // Check for conflicts with existing categories
      const existingAbbs = new Set();
      existingItems.forEach(item => {
        if (item.baseId && item.baseId.length >= 2) {
          // Extract category abbreviation from baseId
          const existingCatAbb = item.baseId.slice(0, 2);
          existingAbbs.add(existingCatAbb);
        }
      });
      
      // If conflict exists, try different combinations
      if (existingAbbs.has(abb)) {
        // Try first and third letter
        if (cleanCat.length >= 3) {
          const alt1 = cleanCat[0] + cleanCat[2];
          if (!existingAbbs.has(alt1)) {
            abb = alt1;
          } else if (cleanCat.length >= 4) {
            // Try first and fourth letter
            const alt2 = cleanCat[0] + cleanCat[3];
            if (!existingAbbs.has(alt2)) {
              abb = alt2;
            } else {
              // Try second and third letter
              const alt3 = cleanCat[1] + cleanCat[2];
              if (!existingAbbs.has(alt3)) {
                abb = alt3;
              }
            }
          }
        }
      }
      
      return abb;
    };
    
    const categoryAbb = generateCategoryAbb(category);
    
    // Get name abbreviation (first 2 characters, uppercase)
    const nameAbb = name.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    
    // Return base ID without sequential number
    return `${categoryAbb}${dateStr}${nameAbb}`;
  };

  // Load custom currencies and preload for formatting
  useEffect(() => {
    const loadCustomCurrencies = async () => {
      if (facilityId) {
        try {
          const currencies = await getCustomCurrencies(facilityId);
          setCustomCurrencies(Object.values(currencies) || []);
          // Preload currencies for proper formatting
          await preloadCustomCurrencies(facilityId);
        } catch (error) {
          console.error('Error loading custom currencies:', error);
        }
      }
    };
    loadCustomCurrencies();
  }, [facilityId]);

  useEffect(() => {
    const itemsRef = ref(database, `inventory/${facilityId}/items`);
    
    const unsubscribe = onValue(itemsRef, (snapshot) => {
      try {
        const itemsData = [];
        if (snapshot.exists()) {
          const data = snapshot.val();
          Object.keys(data).forEach(itemName => {
            itemsData.push({ 
              key: itemName, 
              itemName: itemName,
              ...data[itemName] 
            });
          });
        }
        setItems(itemsData);
        setLoading(false);
      } catch (err) {
        openNotification('error', `Failed to load items: ${err.message}`);
        setLoading(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const handleAddItem = () => {
    setEditingItem(null);
    setSelectedCurrency(userData?.defaultCurrency || 'USD');
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setSelectedCurrency(item.currency || 'USD');
    form.setFieldsValue({
      name: item.name,
      category: item.category,
      price: item.price, // Keep for backward compatibility
      sellingPrice: item.price, // Use price as sellingPrice if purchasingPrice exists, otherwise use price
      purchasingPrice: item.purchasingPrice || null,
      quantity: item.quantity,
      currency: item.currency || 'USD',
      barcode: item.barcode || ''
    });
    setIsModalVisible(true);
  };

  const handleGenerateQR = (item) => {
    setSelectedItemForQR(item);
    setQrModalVisible(true);
  };

  const handleGenerateBarcode = (item) => {
    setSelectedItemForBarcode(item);
    setBarcodeModalVisible(true);
  };

  const handleShowItemDetail = (item) => {
    setSelectedItemForDetail(item);
    setItemDetailModalVisible(true);
  };

  const handleDeleteItem = async (item) => {
    try {
      await remove(ref(database, `inventory/${facilityId}/items/${item.itemName || item.name}`));
      openNotification('success', `Item "${item.name}" deleted successfully`);
    } catch (err) {
      openNotification('error', `Failed to delete item: ${err.message}`);
    }
  };

  const handleQuantityIncrease = async (item) => {
    try {
      const newQuantity = (item.quantity || 0) + 1;
      await update(ref(database, `inventory/${facilityId}/items/${item.itemName || item.name}`), {
        quantity: newQuantity,
        updatedAt: new Date().toISOString()
      });
      openNotification('success', `Added 1 ${item.name} to inventory`);
    } catch (err) {
      openNotification('error', `Failed to update quantity: ${err.message}`);
    }
  };

  const handleQuantityDecrease = async (item) => {
    try {
      const newQuantity = Math.max(0, (item.quantity || 0) - 1);
      await update(ref(database, `inventory/${facilityId}/items/${item.itemName || item.name}`), {
        quantity: newQuantity,
        updatedAt: new Date().toISOString()
      });
      openNotification('success', `Removed 1 ${item.name} from inventory`);
    } catch (err) {
      openNotification('error', `Failed to update quantity: ${err.message}`);
    }
  };

  const handleDirectQuantityChange = async (item, newQuantity) => {
    try {
      await update(ref(database, `inventory/${facilityId}/items/${item.itemName || item.name}`), {
        quantity: Math.max(0, newQuantity || 0),
        updatedAt: new Date().toISOString()
      });
      openNotification('success', `Updated ${item.name} quantity to ${newQuantity}`);
    } catch (err) {
      openNotification('error', `Failed to update quantity: ${err.message}`);
    }
  };

  const handleViewIndividualItem = (individualId) => {
    const url = `${window.location.origin}/${facilityId}/item/${individualId}`;
    window.open(url, '_blank');
    openNotification('success', `Opening individual item: ${individualId}`);
  };

  const handleDownloadIndividualQR = (individualId, itemName) => {
    // Generate QR for individual item
    const qrData = {
      type: 'individual_item',
      facilityId: facilityId,
      itemId: individualId,
      itemName: itemName,
      url: `${window.location.origin}/${facilityId}/item/${individualId}`
    };
    
    // Create and trigger download
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(JSON.stringify(qrData), { width: 512 })
        .then(dataUrl => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `[QR] ${facilityId} - ${individualId}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          openNotification('success', `QR code downloaded for ${individualId}`);
        })
        .catch(err => {
          openNotification('error', `Failed to generate QR: ${err.message}`);
        });
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleDownloadGroupQRZip = async (baseId, quantity, itemName) => {
    try {
      const JSZip = (await import('jszip')).default;
      const QRCode = (await import('qrcode')).default;
      const zip = new JSZip();
      
      // Generate QR codes for all individual items in the group
      for (let i = 1; i <= quantity; i++) {
        const individualId = `${baseId}${i}`;
        const qrData = {
          type: 'individual_item',
          facilityId: facilityId,
          itemId: individualId,
          itemName: itemName,
          url: `${window.location.origin}/${facilityId}/item/${individualId}`
        };
        
        const qrDataUrl = await QRCode.toDataURL(JSON.stringify(qrData), { width: 512 });
        const base64Data = qrDataUrl.split(',')[1];
        zip.file(`${individualId}.png`, base64Data, { base64: true });
      }
      
      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `[QR] ${facilityId} - ${baseId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      openNotification('success', `Downloaded ZIP with ${quantity} QR codes for ${itemName}`);
    } catch (err) {
      openNotification('error', `Failed to generate ZIP: ${err.message}`);
    }
  };

  const handleDeleteIndividualItem = async (individualId, item) => {
    await handleQuantityDecrease(item);
    openNotification('success', `Removed individual item: ${individualId}`);
  };

  // Barcode handling functions
  const handleBarcodeScanned = (barcode) => {
    setScannedBarcode(barcode);
    setBarcodeScannerVisible(false);
    
    // Auto-fill the barcode field if modal is open
    if (isModalVisible) {
      form.setFieldsValue({ barcode: barcode });
      openNotification('success', `Barcode scanned and filled: ${barcode}`);
    } else {
      // Search for item with this barcode
      handleBarcodeSearch(barcode);
    }
  };

  const handleStartBarcodeScanning = () => {
    setBarcodeScannerVisible(true);
  };

  const handleBarcodeSearch = async (barcode) => {
    try {
      // Search for existing item with this barcode
      const itemsRef = ref(database, `inventory/${facilityId}/items`);
      const snapshot = await get(itemsRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const foundItem = Object.entries(data).find(([key, item]) => 
          item.barcode === barcode
        );
        
        if (foundItem) {
          const [itemName, itemData] = foundItem;
          openNotification('success', `Found item: ${itemData.name}`);
          
          // Open item detail modal
          setSelectedItemForDetail({
            key: itemName,
            itemName: itemName,
            ...itemData
          });
          setItemDetailModalVisible(true);
          
          return true;
        } else {
          openNotification('info', 'No item found with this barcode. You can create a new item.');
          return false;
        }
      }
    } catch (err) {
      openNotification('error', `Search failed: ${err.message}`);
      return false;
    }
  };

  const handleSubmit = async (values) => {
    try {
      const now = new Date().toISOString();
      const itemName = values.name.trim();
      
      // Check if item name already exists (for new items)
      if (!editingItem && items.some(item => item.name.toLowerCase() === itemName.toLowerCase())) {
        openNotification('error', `Item "${itemName}" already exists`);
        return;
      }
      
      if (editingItem) {
        // Update existing item
        const oldItemName = editingItem.itemName || editingItem.name;
        
        // Build updated item, preserving existing fields
        const updatedItem = {
          name: itemName,
          price: values.sellingPrice || values.price, // sellingPrice takes precedence, fallback to price for backward compatibility
          purchasingPrice: values.purchasingPrice || editingItem.purchasingPrice || null,
          quantity: values.quantity,
          category: values.category,
          currency: values.currency || editingItem.currency || 'USD', // Use existing currency if form doesn't have it
          barcode: values.barcode || editingItem.barcode || '', // Keep existing barcode if not changed
          createdAt: editingItem.createdAt || now, // Preserve or set creation date
          updatedAt: now
        };
        
        // Preserve old price field for backward compatibility if sellingPrice not provided
        if (!values.sellingPrice && editingItem.price) {
          updatedItem.price = editingItem.price;
        }
        
        // Preserve baseId if it exists (for old items)
        if (editingItem.baseId) {
          updatedItem.baseId = editingItem.baseId;
        }
        
        // Only include id if it exists (newer items might have it)
        if (editingItem.id) {
          updatedItem.id = editingItem.id;
        }
        
        // If name changed, we need to delete old and create new
        if (oldItemName !== itemName) {
          await remove(ref(database, `inventory/${facilityId}/items/${oldItemName}`));
          await set(ref(database, `inventory/${facilityId}/items/${itemName}`), updatedItem);
        } else {
          await set(ref(database, `inventory/${facilityId}/items/${itemName}`), updatedItem);
        }
        
        openNotification('success', `Item "${itemName}" updated successfully`);
      } else {
        // Add new item
        const baseId = generateBaseItemId(values.category, values.name, items);
        const newItem = {
          baseId: baseId,
          name: itemName,
          price: values.sellingPrice || values.price, // sellingPrice takes precedence
          purchasingPrice: values.purchasingPrice || null,
          quantity: values.quantity,
          category: values.category,
          currency: values.currency || 'USD',
          barcode: values.barcode || '', // Store barcode number
          createdAt: now,
          updatedAt: now
        };
        
        // Use item name as the key in Firebase
        await set(ref(database, `inventory/${facilityId}/items/${itemName}`), newItem);
        openNotification('success', `Item "${itemName}" added successfully with Base ID: ${baseId}`);
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      openNotification('error', `Failed to save item: ${err.message}`);
    }
  };

  const columns = [
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 170,
      render: (text) => text ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarcodeOutlined />
          <Text style={{ fontSize: '14px', color: 'var(--lcolor)' }} code>{text}</Text>
        </div>
      ) : (
        <Text type="secondary">No barcode</Text>
      )
    },
    {
      title: 'Individual Items',
      key: 'individualItems',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <IndividualItemsDropdown
            baseId={record.baseId}
            quantity={record.quantity || 0}
            itemName={record.name}
            onViewItem={handleViewIndividualItem}
            onDeleteItem={(individualId) => handleDeleteIndividualItem(individualId, record)}
            onDownloadQR={handleDownloadIndividualQR}
          />
          {/* {(record.quantity || 0) > 0 && (
            <Button 
              size="small" 
              type="dashed"
              icon={<QrcodeOutlined />}
              onClick={() => handleDownloadGroupQRZip(record.baseId, record.quantity, record.name)}
              style={{ width: '100%' }}
            >
              Download All QRs ({record.quantity})
            </Button>
          )} */}
        </Space>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => <Tag color="green">{text}</Tag>,
      filters: [...new Set(items.map(item => item.category))].map(cat => ({
        text: cat,
        value: cat,
      })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Price/Unit',
      dataIndex: 'price',
      key: 'price',
      render: (text, record) => formatPriceWithCurrency(text || 0, record.currency || 'USD', facilityId),
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
    },
    {
      title: 'Quantity Control',
      key: 'quantityControl',
      width: 150,
      render: (_, record) => (
        <QuantityControl
          value={record.quantity || 0}
          onIncrease={() => handleQuantityIncrease(record)}
          onDecrease={() => handleQuantityDecrease(record)}
          onDirectChange={(value) => handleDirectQuantityChange(record, value)}
          itemName={record.name}
          confirmIncrease={true}
          confirmDecrease={true}
          showDirectInput={false}
        />
      )
    },
    {
      title: 'Total Value',
      key: 'totalValue',
      render: (_, record) => formatPriceWithCurrency((record.price || 0) * (record.quantity || 0), record.currency || 'USD', facilityId),
      sorter: (a, b) => ((a.price || 0) * (a.quantity || 0)) - ((b.price || 0) * (b.quantity || 0)),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? new Date(text).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Button 
            type="default" 
            size="small" 
            icon={<BarcodeOutlined />}
            onClick={() => handleGenerateBarcode(record)}
            title="View/Generate Barcode"
          />
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record)}
          />
          <Popconfirm
            title="Delete Item Type"
            description={`Are you sure you want to delete "${record.name}" and all ${record.quantity || 0} individual items? This action cannot be undone.`}
            onConfirm={() => handleDeleteItem(record)}
            okText="Yes, Delete All"
            cancelText="Cancel"
            okButtonProps={{ 
              danger: true,
              style: { background: '#ff4d4f', borderColor: '#ff4d4f' }
            }}
          >
            <Button 
              type="primary" 
              danger 
              size="small" 
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const renderItemCard = (item) => (
    <Card
      key={item.key}
      size="small"
      hoverable
      onClick={() => handleShowItemDetail(item)}
      style={{ 
        height: '160px',
        cursor: 'pointer'
      }}
      bodyStyle={{ 
        padding: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <div>
        <div style={{ 
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '4px',
          overflow: 'hidden',
        }}>
          {item.name}
        </div>
        <div style={{ fontSize: '18px', color: 'var(--primary-color)', fontWeight: 'bold' }}>
          {formatPriceWithCurrency(item.price || 0, item.currency || 'USD', facilityId)}
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: '8px'
      }}>
        <Space size="small">
          <Button 
            type="text" 
            size="small" 
            icon={<QrcodeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleGenerateQR(item);
            }}
            title="Generate QR Code"
          />
          <Button 
            type="text" 
            size="small" 
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditItem(item);
            }}
            title="Edit Item"
          />
          <Popconfirm
            title="Delete Item Type"
            description={`Are you sure you want to delete "${item.name}"?`}
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteItem(item);
            }}
            okText="Yes"
            cancelText="No"
            onClick={(e) => e.stopPropagation()}
          >
            <Button 
              type="text" 
              danger 
              size="small" 
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
              title="Delete Item"
            />
          </Popconfirm>
        </Space>
      </div>
    </Card>
  );

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  }

  return (
    <div style={{ marginTop: '20px' }}>
      {contextHolder}
      
      {/* Header with Search and Add Button - Responsive */}
      <div className="items-section-header">
        <h3 className="items-section-title">Items Management</h3>
        <div className="items-section-controls">
          <Input
            placeholder="Search items or scan barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="items-search-input"
            allowClear
            suffix={
              <Button 
                type="text" 
                size="small"
                icon={<ScanOutlined />}
                onClick={handleStartBarcodeScanning}
                title="Scan barcode to search"
              />
            }
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddItem}
            className="items-add-button"
          >
            <span className="admin-button-text">Add Item</span>
          </Button>
        </div>
      </div>

      {/* Items Display */}
      <DataTable
        data={items.filter(item =>
          !searchTerm || 
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.baseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.barcode && item.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
        )}
        columns={columns}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        renderCard={renderItemCard}
        loading={loading}
        tableProps={{
          scroll: { x: 1200 }
        }}
        gridProps={{
          style: { 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '12px',
            maxHeight: '600px',
            overflowY: 'auto',
            padding: '4px'
          }
        }}
      />

      {/* Add/Edit Item Modal */}
      <FormModal
        title={editingItem ? 'Edit Item' : 'Add New Item'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onSubmit={handleSubmit}
        form={form}
        okText={editingItem ? 'Update Item' : 'Add Item'}
        width={600}
      >
        <Form.Item
          name="name"
          label="Item Name"
          rules={[{ required: true, message: 'Please enter item name' }]}
        >
          <Input placeholder="e.g., Coca Cola" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: 'Please enter category' }]}
        >
          <Input placeholder="e.g., Soft Drinks, Spices, etc." />
        </Form.Item>

        {/* Barcode Input with Scan/Manual Mode */}
        <Form.Item
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarcodeOutlined />
              <span>Barcode</span>
              <Button.Group size="small">
                <Button
                  type={inputMode === 'manual' ? 'primary' : 'default'}
                  size="small"
                  icon={<EditFilled />}
                  onClick={() => setInputMode('manual')}
                >
                  Manual
                </Button>
                <Button
                  type={inputMode === 'scan' ? 'primary' : 'default'}
                  size="small"
                  icon={<ScanOutlined />}
                  onClick={() => setInputMode('scan')}
                >
                  Scan
                </Button>
              </Button.Group>
            </div>
          }
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <Form.Item
              name="barcode"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[
                { 
                  pattern: /^[0-9a-zA-Z\-_]*$/,
                  message: 'Barcode can only contain numbers, letters, hyphens, and underscores'
                }
              ]}
            >
              <Input 
                placeholder={inputMode === 'manual' ? "Enter barcode manually" : "Click scan to capture barcode"}
                disabled={inputMode === 'scan'}
                prefix={<BarcodeOutlined />}
              />
            </Form.Item>
            {inputMode === 'scan' && (
              <Button
                type="primary"
                icon={<ScanOutlined />}
                onClick={handleStartBarcodeScanning}
              >
                Scan
              </Button>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {inputMode === 'manual' 
              ? 'Type or paste the barcode number manually'
              : 'Use camera to scan barcode automatically'
            }
          </div>
        </Form.Item>

        <Form.Item
          name="currency"
            label={<span style={{ color: 'var(--lcolor)' }}>Currency</span>}
          rules={[{ required: true, message: 'Please select currency' }]}
          initialValue={userData?.defaultCurrency || 'USD'}
        >
          <Select 
            placeholder="Select currency"
            onChange={(value) => setSelectedCurrency(value)}
          >
            <Select.Option value="USD">$ USD (US Dollar)</Select.Option>
            <Select.Option value="EUR">€ EUR (Euro)</Select.Option>
            <Select.Option value="RWF">Rwf RWF (Rwandan Franc)</Select.Option>
            {customCurrencies.map(curr => (
              <Select.Option key={curr.code} value={curr.code}>
                {curr.symbol} {curr.name} ({curr.code})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="purchasingPrice"
            label={<span style={{ color: 'var(--lcolor)' }}>Purchasing Price (Cost)</span>}
            tooltip="The price you paid to buy this item from your supplier"
            style={{ flex: 1 }}
          >
            <InputNumber
              min={0}
              step={selectedCurrency === 'RWF' ? 1 : 0.01}
              precision={selectedCurrency === 'RWF' ? 0 : 2}
              placeholder={selectedCurrency === 'RWF' ? '0' : '0.00'}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="sellingPrice"
            label={<span style={{ color: 'var(--lcolor)' }}>Selling Price</span>}
            rules={[{ required: true, message: 'Please enter selling price' }]}
            tooltip="The price customers will pay for this item"
            style={{ flex: 1 }}
          >
            <InputNumber
              min={0}
              step={selectedCurrency === 'RWF' ? 1 : 0.01}
              precision={selectedCurrency === 'RWF' ? 0 : 2}
              placeholder={selectedCurrency === 'RWF' ? '0' : '0.00'}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>

        {/* Backward compatibility: keep price field for old items */}
        {editingItem && editingItem.price && !editingItem.purchasingPrice && (
          <Form.Item
            name="price"
            label="Price (Legacy)"
            style={{ display: 'none' }}
            initialValue={editingItem.price}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Form.Item
          name="quantity"
          label={<span style={{ color: 'var(--lcolor)' }}>Initial Quantity</span>}
          rules={[{ required: true, message: 'Please enter quantity' }]}
        >
          <InputNumber
            min={0}
            placeholder="0"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </FormModal>

      {/* QR Code Modal */}
      <QRCodeModal
        visible={qrModalVisible}
        onClose={() => {
          setQrModalVisible(false);
          setSelectedItemForQR(null);
        }}
        item={selectedItemForQR}
        facilityId={facilityId}
      />

      {/* Barcode Modal */}
      <BarcodeModal
        visible={barcodeModalVisible}
        onClose={() => {
          setBarcodeModalVisible(false);
          setSelectedItemForBarcode(null);
        }}
        item={selectedItemForBarcode}
        facilityId={facilityId}
      />

      {/* Item Detail Modal */}
      {selectedItemForDetail && (
        <Modal
          title={selectedItemForDetail.name}
          open={itemDetailModalVisible}
          onCancel={() => {
            setItemDetailModalVisible(false);
            setSelectedItemForDetail(null);
          }}
          footer={null}
          width={600}
        >
          <div style={{ padding: '16px 0' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Product Code:</Text>
                <Tag color="blue">{selectedItemForDetail.baseId}</Tag>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Category:</Text>
                <Tag color="green">{selectedItemForDetail.category}</Tag>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Barcode:</Text>
                {selectedItemForDetail.barcode ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BarcodeOutlined />
                    <Text code>{selectedItemForDetail.barcode}</Text>
                  </div>
                ) : (
                  <Text type="secondary">No barcode assigned</Text>
                )}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Price per Unit:</Text>
                <Text style={{ fontSize: '18px', color: 'var(--primary-color)' }}>
                  {formatPriceWithCurrency(selectedItemForDetail.price || 0, selectedItemForDetail.currency || 'USD', facilityId)}
                </Text>
              </div>

              <div>
                <Text strong>Individual Items:</Text>
                <div style={{ marginTop: '8px' }}>
                  <IndividualItemsDropdown
                    baseId={selectedItemForDetail.baseId}
                    quantity={selectedItemForDetail.quantity || 0}
                    itemName={selectedItemForDetail.name}
                    onViewItem={handleViewIndividualItem}
                    onDeleteItem={(individualId) => handleDeleteIndividualItem(individualId, selectedItemForDetail)}
                  />
                </div>
              </div>
              
              <div>
                <Text strong>Quantity Control:</Text>
                <div style={{ marginTop: '8px' }}>
                  <QuantityControl
                    value={selectedItemForDetail.quantity || 0}
                    onIncrease={() => handleQuantityIncrease(selectedItemForDetail)}
                    onDecrease={() => handleQuantityDecrease(selectedItemForDetail)}
                    itemName={selectedItemForDetail.name}
                    confirmIncrease={true}
                    confirmDecrease={true}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Total Value:</Text>
                <Text style={{ fontSize: '16px', color: 'var(--success-color)' }}>
                  {formatPriceWithCurrency((selectedItemForDetail.price || 0) * (selectedItemForDetail.quantity || 0), selectedItemForDetail.currency || 'USD', facilityId)}
                </Text>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Created:</Text>
                <Text type="secondary">
                  {selectedItemForDetail.createdAt ? new Date(selectedItemForDetail.createdAt).toLocaleDateString() : 'N/A'}
                </Text>
              </div>
            </Space>
          </div>
        </Modal>
      )}

      {/* Barcode Scanner Modal */}
      <SimpleBarcodeScanner
        visible={barcodeScannerVisible}
        onClose={() => setBarcodeScannerVisible(false)}
        onScan={handleBarcodeScanned}
        title={isModalVisible ? "Scan Barcode for New Item" : "Scan Barcode to Search"}
      />
    </div>
  );
};

export default Items;


